import type { PopularityLike } from './electionBoardUtils'

export type VersusFighter = {
  id: string
  label: string
  subtitle?: string
  imageUrl?: string | null
  barColor: string
  stakePercent: number
  totalStake: number
  totalBets: number
  cornerSide?: 'red' | 'blue' | 'neutral' | null
}

export type VersusUndercardMatch = {
  id: string
  weightLabel?: string
  left: VersusFighter
  right: VersusFighter
}

export type VersusDisplayData = {
  mainEvent: { left: VersusFighter; right: VersusFighter } | null
  undercard: VersusUndercardMatch[]
  totalStake: number
  totalBets: number
}

const CORNER_COLORS = {
  red: '#EF4444',
  blue: '#3B82F6',
  neutral: '#6B7280',
} as const

function toFighter(sel: PopularityLike['selections'][number]): VersusFighter {
  const cornerSide = (sel as { cornerSide?: VersusFighter['cornerSide'] }).cornerSide ?? null
  let barColor = sel.barColor || '#3B82F6'
  if (!sel.barColor && cornerSide && CORNER_COLORS[cornerSide]) {
    barColor = CORNER_COLORS[cornerSide]
  }
  return {
    id: String(sel.selectionId),
    label: sel.label,
    subtitle: sel.subtitle,
    imageUrl: sel.imageUrl,
    barColor,
    stakePercent: sel.stakePercent,
    totalStake: sel.totalStake,
    totalBets: sel.totalBets ?? 0,
    cornerSide,
  }
}

function assignMainCorners(a: VersusFighter, b: VersusFighter): { left: VersusFighter; right: VersusFighter } {
  if (a.cornerSide === 'red' || b.cornerSide === 'blue') {
    return {
      left: a.cornerSide === 'red' ? a : b,
      right: b.cornerSide === 'blue' ? b : a.cornerSide === 'blue' ? a : b,
    }
  }
  if (b.cornerSide === 'red' || a.cornerSide === 'blue') {
    return {
      left: b.cornerSide === 'red' ? b : a,
      right: a.cornerSide === 'blue' ? a : b,
    }
  }
  return { left: a, right: b }
}

export function mapPopularityToVersusDisplay(popularity: PopularityLike): VersusDisplayData {
  const sorted = [...popularity.selections].sort((a, b) => {
    if (b.totalStake !== a.totalStake) return b.totalStake - a.totalStake
    return b.totalBets - a.totalBets
  })

  const fighters = sorted.map(toFighter)
  let mainEvent: VersusDisplayData['mainEvent'] = null
  const undercard: VersusUndercardMatch[] = []

  if (fighters.length >= 2) {
    mainEvent = assignMainCorners(fighters[0], fighters[1])
    const rest = fighters.slice(2)
    for (let i = 0; i < rest.length; i += 2) {
      const left = rest[i]
      const right = rest[i + 1] || placeholderFighter(`${left.id}-vs-tbd`, 'TBD', left.barColor)
      undercard.push({
        id: `${left.id}-${right.id}`,
        weightLabel: left.subtitle?.match(/\d+\s*kg/i)?.[0] || undefined,
        left,
        right,
      })
    }
  } else if (fighters.length === 1) {
    mainEvent = {
      left: fighters[0],
      right: placeholderFighter('tbd', 'รอคู่แข่ง', '#3B82F6'),
    }
  }

  return {
    mainEvent,
    undercard,
    totalStake: popularity.totalStake,
    totalBets: popularity.totalBets,
  }
}

function placeholderFighter(id: string, label: string, barColor: string): VersusFighter {
  return {
    id,
    label,
    subtitle: 'รอประกาศ',
    imageUrl: null,
    barColor,
    stakePercent: 0,
    totalStake: 0,
    totalBets: 0,
    cornerSide: 'neutral',
  }
}

export function buildDemoVersusFromSelections(
  selections: Array<{
    key: string
    label: string
    subtitle?: string
    barColor?: string | null
    selectionCode?: string
    imageUrl?: string | null
    cornerSide?: 'red' | 'blue' | 'neutral' | null
  }>,
  seed = 1,
): VersusDisplayData {
  const wave = Math.sin(seed * 0.6) * 0.06
  const leftRatio = Math.min(0.78, Math.max(0.22, 0.58 + wave))
  const totalStake = 324_500 + (seed % 9) * 8_200
  const totalBets = 1_856 + (seed % 5) * 43

  const toDemoFighter = (sel: typeof selections[number], ratio: number, cornerSide?: VersusFighter['cornerSide']): VersusFighter => {
    const stakePercent = Math.round(ratio * 1000) / 10
    return {
      id: sel.key,
      label: sel.label || 'Fighter',
      subtitle: sel.subtitle,
      imageUrl: sel.imageUrl,
      barColor: sel.barColor || (cornerSide === 'red' ? CORNER_COLORS.red : CORNER_COLORS.blue),
      stakePercent,
      totalStake: Math.round(totalStake * ratio),
      totalBets: Math.max(1, Math.round(totalBets * ratio)),
      cornerSide: cornerSide ?? sel.cornerSide ?? null,
    }
  }

  if (selections.length === 0) {
    return { mainEvent: null, undercard: [], totalStake: 0, totalBets: 0 }
  }

  const mainLeft = selections[0]
  const mainRight = selections[1] || {
    key: 'demo-tbd',
    label: 'รอคู่แข่ง',
    subtitle: 'TBD',
    barColor: CORNER_COLORS.blue,
    cornerSide: 'blue' as const,
  }

  const mainEvent = {
    left: toDemoFighter(mainLeft, leftRatio, mainLeft.cornerSide || 'red'),
    right: toDemoFighter(mainRight, 1 - leftRatio, mainRight.cornerSide || 'blue'),
  }

  const undercard: VersusUndercardMatch[] = []
  const rest = selections.slice(2)
  for (let i = 0; i < rest.length; i += 2) {
    const leftSel = rest[i]
    const rightSel = rest[i + 1] || {
      key: `${leftSel.key}-tbd`,
      label: 'TBD',
      subtitle: 'รอประกาศ',
      barColor: '#6B7280',
    }
    const pairWave = Math.sin((seed + i) * 0.8) * 0.1
    const pairLeft = 0.5 + pairWave
    undercard.push({
      id: `${leftSel.key}-${rightSel.key}`,
      weightLabel: leftSel.subtitle?.match(/\d+\s*kg/i)?.[0] || undefined,
      left: toDemoFighter(leftSel, pairLeft, leftSel.cornerSide || 'red'),
      right: toDemoFighter(rightSel, 1 - pairLeft, rightSel.cornerSide || 'blue'),
    })
  }

  return { mainEvent, undercard, totalStake, totalBets }
}

export function mainEventPercents(main: { left: VersusFighter; right: VersusFighter }) {
  const sum = main.left.stakePercent + main.right.stakePercent
  if (sum <= 0) return { left: 50, right: 50 }
  const left = Math.round((main.left.stakePercent / sum) * 1000) / 10
  return { left, right: Math.round((100 - left) * 10) / 10 }
}
